import { useEffect, useId, useState, type CSSProperties } from "react";
import { getMatchScoreColors, getMatchScoreValue } from "../utils";

export const MatchScoreRing = ({
  value,
  size = "small",
}: {
  value: number | null | undefined;
  size?: "small" | "large";
}) => {
  const score = getMatchScoreValue(value);
  const showScore = typeof value === "number";
  const colors = getMatchScoreColors(score);
  const gradientId = useId();
  const sizeValue = size === "large" ? 78 : 46;
  const thickness = size === "large" ? 10 : 7;
  const radius = (sizeValue - thickness) / 2;
  const circumference = 2 * Math.PI * radius;
  const [animatedScore, setAnimatedScore] = useState(0);

  useEffect(() => {
    let frame = 0;
    const durationMs = 1200 as number;
    const startValue = 0;
    const startTime = performance.now();

    const step = (now: number) => {
      const elapsed = Math.min(durationMs, now - startTime);
      const progress = durationMs > 0 ? elapsed / durationMs : 1;
      const eased = 1 - Math.pow(1 - progress, 3);
      setAnimatedScore(startValue + (score - startValue) * eased);
      if (elapsed < durationMs) {
        frame = requestAnimationFrame(step);
      }
    };

    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [score]);

  const progress = Math.max(0, Math.min(1, animatedScore / 100));
  const dashOffset = circumference * (1 - progress);

  return (
    <div
      className={`matches-score-ring ${size}`}
      style={
        {
          "--size": `${sizeValue}px`,
          "--thickness": `${thickness}px`,
          "--ring-start": colors.start,
          "--ring-end": colors.end,
        } as CSSProperties
      }
    >
      <svg
        viewBox={`0 0 ${sizeValue} ${sizeValue}`}
        className="matches-score-ring-svg"
        role="img"
        aria-label={`Match score ${score}%`}
      >
        <defs>
          <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={colors.start} />
            <stop offset="100%" stopColor={colors.end} />
          </linearGradient>
        </defs>
        <circle
          className="matches-score-ring-track"
          cx={sizeValue / 2}
          cy={sizeValue / 2}
          r={radius}
          strokeWidth={thickness}
        />
        <circle
          className="matches-score-ring-progress"
          cx={sizeValue / 2}
          cy={sizeValue / 2}
          r={radius}
          strokeWidth={thickness}
          stroke={`url(#${gradientId})`}
          strokeDasharray={circumference}
          strokeDashoffset={dashOffset}
        />
      </svg>
      <span>{showScore ? `${Math.round(animatedScore)}%` : ""}</span>
    </div>
  );
};
